import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { type ChangeEvent, useRef, useState } from "react";
import * as api from "./api";
import { IMPORT_COLUMNS } from "./columns";
import { go } from "./hooks";
import { IMPORT_MAX_BYTES, importSummary } from "./leads";
import { type Saved, saveFailure } from "./save";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";

function ImportTable({ preview, base }: { preview: api.ImportPreview; base: string }) {
  const [sort, setSort] = useSort("isort", IMPORT_COLUMNS);
  return (
    <>
      <SortSelect id="import-select" columns={IMPORT_COLUMNS} sort={sort} onSort={setSort} fallback="File order" />
      <div className="table-scroll" tabIndex={0} role="region" aria-label="Rows of the file">
        <table className="list import-table" data-testid="import-table" data-cols="import-select">
          <thead>
            <tr>
              {IMPORT_COLUMNS.map((column) => (
                <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {sortRows(preview.rows, IMPORT_COLUMNS, sort).map((row) => (
              <tr key={row.line} data-testid="import-row" data-bad={row.errors.length > 0 || undefined}>
                <th scope="row">{row.line}</th>
                <td data-label="Name">{row.fields.name || "–"}</td>
                <td data-label="Address" title={row.fields.address}>
                  <span className="clip">{row.fields.address || "–"}</span>
                </td>
                <td data-label="Owner" title={row.fields.owner}>
                  <span className="clip name">{row.fields.owner || (row.owner_id ? "You" : "Inbox")}</span>
                </td>
                <td data-label="Check" className="wrap">
                  {row.errors.map((error) => (
                    <span key={error} className="error" data-testid="import-error">
                      {error}{" "}
                    </span>
                  ))}
                  {row.repeats.length > 0 && <span className="chip-flag long">Same as line {row.repeats.join(", ")}</span>}
                  {row.matches.map((match) => (
                    <span key={match.id} className="chip-flag long" data-testid="import-match">
                      Possible match:{" "}
                      <a href={`${base}/${match.kind === "lead" ? "leads" : "accounts"}/${match.id}`} onClick={go}>
                        {match.kind === "account" ? `account ${match.number} ${match.name}` : `lead ${match.name}`}
                      </a>
                    </span>
                  ))}
                  {!row.errors.length && !row.repeats.length && !row.matches.length && "OK"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** Import prospects from a CSV file (F-SAL-1, I-6): a preview with the errors first, then one import. */
export function LeadImport({ base, auth, user, onDone, onCancel }: { base: string; auth: Auth; user: User; onDone: () => void; onCancel: () => void }) {
  const [csv, setCsv] = useState<string | null>(null);
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState<api.ImportPreview | null>(null);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [busy, setBusy] = useState(false);
  const importId = useRef<string | null>(null);
  const pickFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setPreview(null);
    setSaved(null);
    importId.current = null;
    if (!file) return setCsv(null);
    if (file.size > IMPORT_MAX_BYTES) {
      setCsv(null);
      return setSaved({ ok: false, field: true, text: `The file can have ${IMPORT_MAX_BYTES / 1_000_000} MB at most.` });
    }
    setFileName(file.name);
    setCsv(await file.text());
  };
  const check = async () => {
    if (csv === null) return setSaved({ ok: false, field: true, text: "Choose a CSV file." });
    setBusy(true);
    setSaved(null);
    try {
      setPreview(await api.previewLeadImport(await user.getIdToken(), csv));
    } catch (e) {
      setPreview(null);
      setSaved(saveFailure(e, "The check"));
    } finally {
      setBusy(false);
    }
  };
  const run = async () => {
    if (csv === null || !preview || preview.bad) return;
    importId.current ??= crypto.randomUUID();
    setBusy(true);
    setSaved(null);
    try {
      const done = await api.importLeads(await user.getIdToken(), importId.current, csv);
      setSaved({ ok: true, text: `Imported ${done.imported} leads from ${fileName}.` });
      setPreview(null);
      setCsv(null);
      importId.current = null;
      onDone();
    } catch (e) {
      setSaved(saveFailure(e, "The import"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="lead-form import-form" data-testid="lead-import" aria-labelledby="lead-import-title">
      <h2 id="lead-import-title">Import prospects from a file</h2>
      <p className="sub">
        A CSV file. The first row names the columns: name, address, phone, qualifier, previous collector, competitor onsite (yes or no) and owner (an email).
        Only name is necessary.
      </p>
      <label htmlFor="import-file">CSV file</label>
      <input id="import-file" data-testid="import-file" type="file" accept=".csv,text/csv" onChange={(e) => void pickFile(e)} />
      <div className="actions">
        <button type="button" data-testid="import-check" disabled={busy || csv === null} onClick={() => void check()}>
          {busy && !preview ? "Checking…" : "Check the file"}
        </button>
        {preview && (
          <button type="button" className="primary" data-testid="import-run" disabled={busy || preview.bad > 0} onClick={() => void run()}>
            {busy ? "Importing…" : `Import ${preview.rows.length} leads`}
          </button>
        )}
        <button type="button" data-testid="import-cancel" disabled={busy} onClick={onCancel}>
          {saved?.ok ? "Close" : "Cancel"}
        </button>
      </div>
      <p role="status" data-testid="import-status" className={saved && !saved.ok ? "error" : undefined}>
        {saved?.text ?? (preview ? importSummary(preview.rows.length, preview.bad, preview.ignored) : "")}
        {saved && !saved.ok && saved.session && (
          <>
            {" "}
            <button type="button" onClick={() => signOut(auth)}>
              Sign in again
            </button>
          </>
        )}
      </p>
      {preview && <ImportTable preview={preview} base={base} />}
    </section>
  );
}
